import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import { Calendar } from '../ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.tsx";
import CheckboxField from './CheckboxField';
import InputField from './InputField';
import PasswordField from './PasswordField';

export default function SignUpForm({ onSubmit, isLoading }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organizationName: '',
    country: '',
    address: '',
    dob: undefined,
    dobFormatted: null,
    password: '',
    agreeToTerms: false,
  });
  const [error, setError] = useState(null);

  const countries = [
    { value: "nigeria", label: "Nigeria" },
    { value: "united-states", label: "United States" },
    { value: "united-kingdom", label: "United Kingdom" },
    { value: "canada", label: "Canada" },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      dob: date,
      dobFormatted: date ? format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null,
    }));
  };

  const handleCountryChange = (value) => {
    setFormData(prev => ({
      ...prev,
      country: value
    }));
  };

  const validateForm = () => {
    if (!formData.agreeToTerms) {
      return 'Please agree to the terms and conditions';
    }
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!formData.dob) {
      return 'Please select your date of birth';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const countryLabel = countries.find(c => c.value === formData.country)?.label || formData.country;

    const submissionData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      organizationName: formData.organizationName,
      country: countryLabel,
      address: formData.address,
      dob: formData.dobFormatted,
    };

    try {
      await onSubmit(submissionData);
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Sign up</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InputField
          label="First Name"
          id="firstName"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          placeholder="John"
          required
        />
        <InputField
          label="Last Name"
          id="lastName"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          placeholder="Doe"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InputField
          label="Email"
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="example@mail.com"
          required
        />
        <InputField
          label="Phone Number"
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="00023437484"
          required
        />
      </div>

      <InputField
        label="Organization Name"
        id="organizationName"
        name="organizationName"
        value={formData.organizationName}
        onChange={handleChange}
        placeholder="Your Organization"
        required
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label
            htmlFor="country"
            className="block text-sm font-medium text-gray-700"
          >
            Country
          </label>
          <Select
            value={formData.country}
            onValueChange={handleCountryChange}
            required
          >
            <SelectTrigger id="country" className="w-full">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country.value} value={country.value}>
                  {country.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <InputField
          label="Address"
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="123 Main St, City, State, ZIP"
          required
        />
      </div>

      <div className="space-y-2">
        <label
          htmlFor="dob"
          className="block text-sm font-medium text-gray-700"
        >
          Date of Birth
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="dob"
              variant="outline"
              className={cn(
                'w-full justify-start text-left font-normal h-10 px-3 py-2',
                !formData.dob && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formData.dob ? (
                format(formData.dob, 'PPP')
              ) : (
                <span>Pick a date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 bg-white shadow-lg rounded-md"
            align="start"
          >
            <Calendar
              mode="single"
              selected={formData.dob}
              onSelect={handleDateChange}
              disabled={(date) =>
                date > new Date() || date < new Date('1900-01-01')
              }
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <PasswordField
        label="Password"
        id="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        required
      />

      <CheckboxField
        id="agreeToTerms"
        name="agreeToTerms"
        checked={formData.agreeToTerms}
        onChange={handleChange}
        required
      >
        I agree to platform's{' '}
        <Link to="/terms" className="text-purple-600 hover:text-purple-700">
          Terms of service
        </Link>{' '}
        and{' '}
        <Link to="/privacy" className="text-purple-600 hover:text-purple-700">
          Privacy Policy
        </Link>
      </CheckboxField>

      {error && (
        <div className="text-red-600 text-sm mt-2 text-center">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Link
          to="/"
          className="w-full flex justify-center py-2 px-4 border border-purple-600 rounded-md shadow-sm text-sm font-medium text-purple-600 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          Back
        </Link>
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            isLoading
              ? 'bg-purple-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
          }`}
        >
          {isLoading ? 'Processing...' : 'Next'}
        </button>
      </div>
    </form>
  );
}

SignUpForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};
